import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

class SportTreeMenu extends HTMLElement {
  /** @type {HTMLElement | null} */
  #menu = null;

  /** @type {HTMLElement | null} */
  #header = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #animationFrame = null;

  #controller = new AbortController();

  #resizeObserver = new ResizeObserver(() => {
    this.#updatePlaceholderHeight();
    this.#scheduleUpdate();
  });

  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    this.#menu = this.querySelector('.sport-tree-menu');
    if (!this.#menu) return;

    const { signal } = this.#controller;
    const header = document.querySelector('#header-component');
    this.#header = header instanceof HTMLElement ? header : null;

    this.#resizeObserver.observe(this.#menu);
    if (this.#header) this.#resizeObserver.observe(this.#header);

    window.addEventListener('resize', this.#scheduleUpdate, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange, { signal });

    this.#bindScrollTarget();
    this.#updatePlaceholderHeight();
    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#scrollTarget?.removeEventListener('scroll', this.#scheduleUpdate);

    if (this.#animationFrame !== null) cancelAnimationFrame(this.#animationFrame);
  }

  #bindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#scheduleUpdate);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#scheduleUpdate, { passive: true });
  }

  #handleBreakpointChange = () => {
    this.#bindScrollTarget();
    this.#scheduleUpdate();
  };

  #updatePlaceholderHeight() {
    if (!this.#menu) return;

    this.style.setProperty('--sport-tree-menu-height', `${Math.round(this.#menu.offsetHeight)}px`);
  }

  #scheduleUpdate = () => {
    if (this.#animationFrame !== null) return;

    this.#animationFrame = requestAnimationFrame(() => {
      this.#animationFrame = null;
      this.#updateStickyState();
    });
  };

  #updateStickyState() {
    const headerHeight = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const stickyMode = this.#header?.getAttribute('sticky');
    const headerIsVisible =
      stickyMode === 'always' ||
      (stickyMode === 'scroll-up' && this.#header?.dataset.stickyState === 'active');
    const stickyOffset = headerIsVisible ? headerHeight - 1 : 0;
    const shouldStick = this.getBoundingClientRect().top <= stickyOffset;

    this.style.setProperty('--sport-tree-menu-sticky-offset', `${Math.max(stickyOffset, 0)}px`);
    this.toggleAttribute('data-sticky', shouldStick);
  }
}

if (!customElements.get('sport-tree-menu')) {
  customElements.define('sport-tree-menu', SportTreeMenu);
}
